import { useState } from 'react';
import { ArrowLeft, Bell, Zap, TrendingUp, Clock, CheckCircle2 } from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// Signal type options
const signalTypes = [
  { value: 'pr_review_time', label: 'PR Review Time', icon: Clock },
  { value: 'design_handoff', label: 'Design Handoff', icon: Zap },
  { value: 'sprint_velocity', label: 'Sprint Velocity', icon: TrendingUp },
  { value: 'custom', label: 'Custom', icon: Bell },
];

// Condition options
const conditions = [
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' },
  { value: 'equal_to', label: 'Equal to' },
  { value: 'changes_by', label: 'Changes by' },
];

// Pre-built templates
const signalTemplates = [
  {
    id: 1,
    name: 'Slow PR Reviews',
    description: 'Alert when PRs take more than 24 hours to review',
    type: 'pr_review_time',
    condition: 'greater_than',
    threshold: 24,
    unit: 'hours',
    icon: Clock,
  },
  {
    id: 2,
    name: 'Velocity Drop',
    description: 'Alert when sprint velocity drops by more than 20%',
    type: 'sprint_velocity',
    condition: 'changes_by',
    threshold: -20,
    unit: '%',
    icon: TrendingUp,
  },
  {
    id: 3,
    name: 'Design Sync',
    description: 'Alert when design files are updated',
    type: 'design_handoff',
    condition: 'changes_by',
    threshold: 1,
    unit: 'files',
    icon: Zap,
  },
];

export default function CreateSignalPage() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    type: 'pr_review_time',
    description: '',
    threshold: '',
    condition: 'greater_than',
    unit: 'hours',
    notifications: {
      email: true,
      slack: false,
      inApp: true,
    },
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNotificationChange = (type) => {
    setFormData((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [type]: !prev.notifications[type],
      },
    }));
  };

  const handleTemplateSelect = (template) => {
    setFormData({
      name: template.name,
      type: template.type,
      description: template.description,
      threshold: template.threshold.toString(),
      condition: template.condition,
      unit: template.unit,
      notifications: formData.notifications,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Creating signal:', formData);
    // In real implementation, this would save to backend
    navigate(projectId ? `/projects/${projectId}` : '/signals');
  };

  const selectedType = signalTypes.find((t) => t.value === formData.type);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to={projectId ? `/projects/${projectId}` : '/signals'}>
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold">Create Signal</h1>
            <p className="text-muted-foreground">
              Set up automated alerts for important project events
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit}>
              <Card className="glass-elevated p-6 space-y-6">
                {/* Signal Name */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold">Signal Name</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., Slow PR Review Alert"
                    className="glass-input"
                    required
                  />
                </div>

                {/* Signal Type */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold">Signal Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    {signalTypes.map((type) => {
                      const Icon = type.icon;
                      const isSelected = formData.type === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => handleInputChange('type', type.value)}
                          className={cn(
                            'glass-subtle p-4 rounded-lg border-2 transition-all hover:border-primary/50',
                            isSelected
                              ? 'border-primary bg-primary/10'
                              : 'border-transparent'
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <Icon
                              className={cn(
                                'w-5 h-5',
                                isSelected ? 'text-primary' : 'text-muted-foreground'
                              )}
                            />
                            <span className="font-medium text-sm">
                              {type.label}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold">
                    Description (Optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange('description', e.target.value)
                    }
                    placeholder="Describe what this signal monitors..."
                    className="w-full glass-input px-4 py-3 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={3}
                  />
                </div>

                {/* Threshold Configuration */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold">
                    Trigger Condition
                  </label>
                  <div className="flex gap-3">
                    <select
                      value={formData.condition}
                      onChange={(e) =>
                        handleInputChange('condition', e.target.value)
                      }
                      className="glass-input px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {conditions.map((cond) => (
                        <option key={cond.value} value={cond.value}>
                          {cond.label}
                        </option>
                      ))}
                    </select>
                    <Input
                      type="number"
                      value={formData.threshold}
                      onChange={(e) =>
                        handleInputChange('threshold', e.target.value)
                      }
                      placeholder="0"
                      className="glass-input flex-1"
                      required
                    />
                    <Input
                      value={formData.unit}
                      onChange={(e) => handleInputChange('unit', e.target.value)}
                      placeholder="hours"
                      className="glass-input w-32"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Alert me when the value {formData.condition.replace('_', ' ')}{' '}
                    {formData.threshold || '___'} {formData.unit || 'units'}
                  </p>
                </div>

                {/* Notification Preferences */}
                <div className="space-y-3">
                  <label className="text-sm font-semibold">
                    Notification Preferences
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-3 glass-subtle rounded-lg cursor-pointer hover:bg-accent/50 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.notifications.email}
                        onChange={() => handleNotificationChange('email')}
                        className="w-4 h-4 rounded border-2 border-primary text-primary focus:ring-2 focus:ring-primary"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">Email</div>
                        <div className="text-xs text-muted-foreground">
                          Receive alerts via email
                        </div>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-3 glass-subtle rounded-lg cursor-pointer hover:bg-accent/50 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.notifications.slack}
                        onChange={() => handleNotificationChange('slack')}
                        className="w-4 h-4 rounded border-2 border-primary text-primary focus:ring-2 focus:ring-primary"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">Slack</div>
                        <div className="text-xs text-muted-foreground">
                          Post alerts to Slack channel
                        </div>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-3 glass-subtle rounded-lg cursor-pointer hover:bg-accent/50 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.notifications.inApp}
                        onChange={() => handleNotificationChange('inApp')}
                        className="w-4 h-4 rounded border-2 border-primary text-primary focus:ring-2 focus:ring-primary"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">In-App</div>
                        <div className="text-xs text-muted-foreground">
                          Show notifications in FocusPane
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex gap-3 pt-4">
                  <Button type="submit" size="lg" className="flex-1">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Create Signal
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                </div>
              </Card>
            </form>
          </div>

          {/* Templates Sidebar */}
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold mb-2">Quick Templates</h2>
              <p className="text-sm text-muted-foreground">
                Start with a pre-built signal template
              </p>
            </div>
            <div className="space-y-3">
              {signalTemplates.map((template) => {
                const Icon = template.icon;
                return (
                  <Card
                    key={template.id}
                    className="glass-subtle p-4 hover:glass-elevated transition-all cursor-pointer"
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm mb-1">
                          {template.name}
                        </h3>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {template.description}
                        </p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
